/* eslint-disable @typescript-eslint/indent */
import React, { FC, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import {
  OccupationReferenceExtended,
  Vessel as SeagoingVessel,
  VesselExternal,
} from '@sdir/httpclient/lib/clients/aps/seagoing';
import { Country } from '@sdir/httpclient/lib/clients/core/search';
import { SdiAdd, SdirIconButton, TextLink } from '@sdir/sds';
import { useNumberService } from '@sdir/utilities/lib/hooks';
import moment from 'moment';
import { Form, Formik, FormikErrors, FormikHelpers } from 'formik';
import { AddedSeagoing, AddedSeagoingDetails, VesselSubformInput } from '../../../../types';
import { SeagoingSummary } from './SeagoingSummary';
import { createInitialVesselInput, validateVesselInput, VesselSubForm } from './VesselSubForm';
import {
  createSeagoingDetailsInitialValues,
  SeagoingDetailsInput,
  SeagoingDetailsSubForm,
  validateSeagoingDetails,
} from './SeagoingDetailsSubForm';
import { formatCountryOptions } from '../../../../helpers/formattingHelpers';

const createSeagoingVesselDto = (vesselInput: VesselSubformInput): VesselExternal => {
  if (vesselInput.useExisting) {
    return {
      name: vesselInput.existingVessel?.name,
      imoNumber: vesselInput.existingVessel?.imoNumber,
      callSign: vesselInput.existingVessel?.callSign,
      // backend requires countryCode if imoNumber is missing
      // -> assume NOR for these cases, as country is not part of search index model
      countryCode: vesselInput.existingVessel?.imoNumber ? null : 'NOR',
    };
  }
  return {
    name: vesselInput.newVessel.name,
    imoNumber: vesselInput.newVessel.imoNumber,
    callSign: vesselInput.newVessel.callSign,
    countryCode: vesselInput.newVessel.country,
  };
};

interface SeagoingEditorProps {
  seagoing: AddedSeagoing[];
  countries: Country[];
  occupations: OccupationReferenceExtended[];
  onSeagoingChange: (newData: AddedSeagoing[]) => void;
}

type SeagoingEditorInput = {
  vessel: VesselSubformInput;
  details: SeagoingDetailsInput;
};

export const SeagoingEditor: React.FC<SeagoingEditorProps> = ({
  seagoing,
  countries,
  occupations,
  onSeagoingChange,
}) => {
  const intl = useIntl();
  const numberService = useNumberService();
  const [addMoreOpen, setAddMoreOpen] = useState(false);

  const countryOptions = useMemo(() => {
    return formatCountryOptions(countries);
  }, [countries]);

  const handleUpdateSeagoing = useCallback(
    (index: number, updated: AddedSeagoing) =>
      onSeagoingChange([...seagoing.slice(0, index), updated, ...seagoing.slice(index + 1)]),
    [onSeagoingChange, seagoing]
  );

  const handleAddSeagoing = useCallback(
    (newSeagoing: AddedSeagoing) => onSeagoingChange([...seagoing, newSeagoing]),
    [onSeagoingChange, seagoing]
  );

  const handleRemoveSeagoing = useCallback(
    (index: number) =>
      onSeagoingChange([...seagoing.slice(0, index), ...seagoing.slice(index + 1)]),
    [onSeagoingChange, seagoing]
  );

  const handleAddNewDetailForVessel = useCallback(
    (vessel: SeagoingVessel, addedDetails: AddedSeagoingDetails) => {
      const existingMatchIndex = seagoing.findIndex(
        (x) => x.vessel.callSign === vessel?.callSign && x.vessel.imoNumber === vessel?.imoNumber
      );
      if (existingMatchIndex >= 0) {
        const existingMatch = seagoing[existingMatchIndex];
        handleUpdateSeagoing(existingMatchIndex, {
          ...existingMatch,
          details: [...existingMatch.details, addedDetails],
        });
      } else {
        handleAddSeagoing({ vessel, details: [addedDetails] });
      }
    },
    [handleAddSeagoing, handleUpdateSeagoing, seagoing]
  );

  const handleSubmit = useCallback(
    (values: SeagoingEditorInput, formikHelpers: FormikHelpers<SeagoingEditorInput>) => {
      const vesselNewDetail = createSeagoingVesselDto(values.vessel);

      if (!values.details.startDate || !values.details.endDate) return;

      let totalHours: number;
      if (values.details.useHoursPerDay) {
        const hoursPerDay = numberService.parseNumber(values.details.hoursPerDay, 0);
        const periodDiff = values.details.endDate.diff(values.details.startDate);
        const totalDays = moment.duration(periodDiff).asDays() + 1;
        totalHours = hoursPerDay * totalDays;
      } else {
        totalHours = numberService.parseNumber(values.details.totalHours, 0);
      }

      const addedDetails: AddedSeagoingDetails = {
        occupationCode: values.details.occupationCode,
        startDate: values.details.startDate,
        endDate: values.details.endDate,
        totalHours,
        existingReport: null,
      };

      handleAddNewDetailForVessel(vesselNewDetail, addedDetails);

      formikHelpers.resetForm({
        values: {
          ...values,
          details: {
            ...createSeagoingDetailsInitialValues(),
            useHoursPerDay: values.details.useHoursPerDay,
          },
        },
      });
    },
    [handleAddNewDetailForVessel, numberService]
  );

  const handleOpenAddMore = useCallback(() => setAddMoreOpen(true), []);

  const handleValidate = useCallback(
    (values: SeagoingEditorInput) => {
      const errors: FormikErrors<SeagoingEditorInput> = {};

      const vesselErrors = validateVesselInput(values.vessel, intl, [
        'name',
        'imoNumber',
        'country',
      ]);
      if (vesselErrors) errors.vessel = vesselErrors;

      const detailsErrors = validateSeagoingDetails(values.details, intl, numberService);
      if (detailsErrors) errors.details = detailsErrors;

      return Object.keys(errors).length ? errors : undefined;
    },
    [intl, numberService]
  );

  const initialValues: SeagoingEditorInput = {
    vessel: createInitialVesselInput(),
    details: createSeagoingDetailsInitialValues(),
  };

  return (
    <Container>
      {seagoing.map((s, i) => (
        <SummaryRow
          key={`${s.vessel.callSign}:${s.vessel.imoNumber}`}
          index={i}
          occupations={occupations}
          countries={countries}
          seagoing={s}
          onUpdate={handleUpdateSeagoing}
          onRemove={handleRemoveSeagoing}
        />
      ))}
      <Formik initialValues={initialValues} onSubmit={handleSubmit} validate={handleValidate}>
        <Form noValidate>
          {addMoreOpen || !seagoing.length ? (
            <div className="seagoing-forms">
              <VesselSubForm
                name="vessel"
                countryOptions={countryOptions}
                requiredFields={['name', 'imoNumber', 'country']}
              />
              <SeagoingDetailsSubForm name="details" occupations={occupations} />
              <div className="details-form-controls">
                <SdirIconButton
                  type="ghost"
                  text={intl.formatMessage({ id: 'addseagoing.details.add' })}
                  icon={SdiAdd}
                />
              </div>
            </div>
          ) : (
            <AddMoreButton
              text={intl.formatMessage({ id: 'addseagoing.addmoreseagoingtime' })}
              onClick={handleOpenAddMore}
            />
          )}
        </Form>
      </Formik>
    </Container>
  );
};

type SummaryRowProps = {
  index: number;
  occupations: OccupationReferenceExtended[];
  seagoing: AddedSeagoing;
  countries: Country[];
  onUpdate: (index: number, updatedSeagoing: AddedSeagoing) => void;
  onRemove: (index: number) => void;
};

const SummaryRow: FC<SummaryRowProps> = ({ index, onUpdate, onRemove, ...rest }) => {
  const handleUpdate = useCallback((v: AddedSeagoing) => onUpdate(index, v), [index, onUpdate]);
  const handleRemove = useCallback(() => onRemove(index), [index, onRemove]);
  return <SeagoingSummary {...rest} onUpdate={handleUpdate} onRemove={handleRemove} />;
};

const Container = styled.div`
  .seagoing-forms {
    max-width: 50rem;
  }

  .details-form-controls {
    display: flex;
    justify-content: flex-end;
  }
`;

export default SeagoingEditor;

const AddMoreButton = ({ text, onClick }) => {
  return <TextLink text={text} click={onClick} icon={<SdiAdd />} />;
};
